export interface EnvironmentConfig {
  apiBaseUrl: string;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  enableAnalytics: boolean;
  recaptchaSiteKey: string;
  mainSiteUrl: string;
  gtmContainerId: string;
  gtmAuth: string;
  gtmPreview: string;
  /** Cove support widget: tenant-scoped embed script URL. */
  coveEmbedUrl: string;
  /** Cove support widget id. Blank disables the portal's "Need Help?" control. */
  coveWidgetId: string;
}

// TODO: enable analytics for production and staging after approval
export const environments: Record<string, EnvironmentConfig> = {
  local: {
    apiBaseUrl: 'https://kuow-api.localto.net',
    logLevel: 'debug',
    enableAnalytics: false,
    recaptchaSiteKey: '6Lc6QfQrAAAAAExUcKtYc7GoKXtSHqnVuNF6WNAm',
    mainSiteUrl: 'http://localhost:3000',
    gtmContainerId: 'GTM-THDGKGX',
    gtmAuth: '1DZPKFPskrUn7Qnysz4YHQ',
    gtmPreview: 'env-10',
    coveEmbedUrl: 'https://api-dev.coveanswers.com/t/kuow/widget/embed.js',
    coveWidgetId: '881b519f-b1d4-4885-aac4-0dabf92fdc32',
  },
  development: {
    apiBaseUrl: 'https://core-dev.kuow.io',
    logLevel: 'debug',
    enableAnalytics: true,
    recaptchaSiteKey: '6Lc6QfQrAAAAAExUcKtYc7GoKXtSHqnVuNF6WNAm',
    mainSiteUrl: 'https://dev.kuow.org',
    gtmContainerId: 'GTM-THDGKGX',
    gtmAuth: '1DZPKFPskrUn7Qnysz4YHQ',
    gtmPreview: 'env-10',
    coveEmbedUrl: 'https://api-dev.coveanswers.com/t/kuow/widget/embed.js',
    coveWidgetId: '881b519f-b1d4-4885-aac4-0dabf92fdc32',
  },
  staging: {
    apiBaseUrl: 'https://core-stage.kuow.io',
    logLevel: 'info',
    enableAnalytics: true,
    recaptchaSiteKey: '6Lc6QfQrAAAAAExUcKtYc7GoKXtSHqnVuNF6WNAm',
    mainSiteUrl: 'https://stage.kuow.org',
    gtmContainerId: 'GTM-THDGKGX',
    gtmAuth: '1DZPKFPskrUn7Qnysz4YHQ',
    gtmPreview: 'env-10',
    coveEmbedUrl: 'https://api-staging.coveanswers.com/t/kuow/widget/embed.js',
    coveWidgetId: '881b519f-b1d4-4885-aac4-0dabf92fdc32',
  },
  production: {
    apiBaseUrl: 'https://core-prod.kuow.io',
    logLevel: 'error',
    enableAnalytics: true,
    recaptchaSiteKey: '6Lc6QfQrAAAAAExUcKtYc7GoKXtSHqnVuNF6WNAm',
    mainSiteUrl: 'https://www.kuow.org',
    gtmContainerId: 'GTM-THDGKGX',
    gtmAuth: '_Q9voBcxAF3vI2TTx3WxXg',
    gtmPreview: 'env-2',
    coveEmbedUrl: 'https://api.coveanswers.com/t/kuow/widget/embed.js',
    coveWidgetId: '881b519f-b1d4-4885-aac4-0dabf92fdc32',
  },
};

export function getCurrentEnvironment(): string {
  const viteEnv = (import.meta as any).env;

  if (viteEnv && typeof viteEnv.VITE_ENVIRONMENT === 'string') {
    return viteEnv.VITE_ENVIRONMENT;
  }

  return 'development';
}

/**
 * A `VITE_*` value is host-supplied text; only a non-blank string overrides the
 * table. `raw` is `unknown` because `import.meta.env` is untyped here and is
 * narrowed immediately.
 */
function envOverride(raw: unknown, fallback: string): string {
  return typeof raw === 'string' && raw.trim() !== '' ? raw : fallback;
}

/**
 * Overlay build-time `VITE_*` overrides onto a table row: the API base for
 * Storybook and the Cove pair so a local or preview build can point at any
 * tenant without editing the table. Pure so it can be tested — Vite inlines
 * `import.meta.env` per module, so it cannot be stubbed from a test.
 */
export function applyEnvOverrides(config: EnvironmentConfig, viteEnv: unknown): EnvironmentConfig {
  if (typeof viteEnv !== 'object' || viteEnv === null) return config;
  const env = viteEnv as Record<string, unknown>;
  return {
    ...config,
    apiBaseUrl: envOverride(env.VITE_API_BASE_URL, config.apiBaseUrl),
    coveEmbedUrl: envOverride(env.VITE_COVE_EMBED_URL, config.coveEmbedUrl),
    coveWidgetId: envOverride(env.VITE_COVE_WIDGET_ID, config.coveWidgetId),
  };
}

export function getConfig(): EnvironmentConfig {
  const env = getCurrentEnvironment();
  const config = environments[env] ?? environments.development;
  return applyEnvOverrides(config, (import.meta as any).env);
}

export interface CoveSupportConfig {
  embedUrl: string;
  widgetId: string;
}

/**
 * The Cove pair from a resolved config, or null when either half is blank.
 * Null means "feature off" — callers never see a half-configured pair.
 */
export function resolveCoveSupportConfig(config: EnvironmentConfig): CoveSupportConfig | null {
  const embedUrl = config.coveEmbedUrl.trim();
  const widgetId = config.coveWidgetId.trim();
  if (embedUrl === '' || widgetId === '') return null;
  return { embedUrl, widgetId };
}

/** The Cove pair for this build. See `resolveCoveSupportConfig`. */
export function getCoveSupportConfig(): CoveSupportConfig | null {
  return resolveCoveSupportConfig(getConfig());
}
